import React, { useState } from 'react';
import { HelpCircle, Plus, Minus, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header21';
import Footer from './components/Footer21';

const FAQs21 = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [openIndex, setOpenIndex] = useState(null);

    const categories = [
        {
            label: 'Orders & Shipping',
            questions: [
                {
                    question: 'How long does it take for my order to be delivered?',
                    answer: 'Standard shipping within India takes 5-7 business days, while Express shipping delivers within 2-3 business days. For custom-made or bespoke garments, please allow an additional 7-21 business days for production before shipping. International orders typically take 7-14 business days depending on the destination country.'
                },
                {
                    question: 'Do you deliver to my pincode?',
                    answer: 'We deliver to over 25,000 pincodes across India through our logistics partner Shiprocket. You can check delivery availability by entering your pincode on the product page or during checkout. For remote areas, delivery may take an additional 2-3 business days. International shipping is available to select countries.'
                },
                {
                    question: 'How can I track my order?',
                    answer: 'Once your order is dispatched, you will receive a shipping confirmation email and SMS with a tracking link. You can also track your order by logging into your account and visiting the "My Orders" section. Tracking updates are refreshed in real-time via our Shiprocket integration.'
                },
                {
                    question: 'Can I change my delivery address after placing an order?',
                    answer: 'Address changes can be accommodated if the order has not yet been dispatched. Please contact our customer support team immediately via WhatsApp or email with your order number and the updated address. Once an order is shipped, the delivery address cannot be changed.'
                },
                {
                    question: 'What happens if I am not available to receive my order?',
                    answer: 'Our delivery partner will make up to 3 delivery attempts. If you are unavailable, the courier will leave a notification and attempt redelivery on the next business day. After 3 failed attempts, the package will be returned to our warehouse and we will contact you to arrange reshipment.'
                }
            ]
        },
        {
            label: 'Returns & Exchange',
            questions: [
                {
                    question: 'What is your exchange policy?',
                    answer: 'We accept exchange requests within 7 days of delivery. Items must be unused, unwashed, and in their original condition with all tags and packaging intact. Custom-made garments, intimate wear, and items marked as "Final Sale" are not eligible for exchange. Exchanges are subject to availability of the requested size or colour.'
                },
                {
                    question: 'How do I initiate an exchange?',
                    answer: 'Log into your account, navigate to "My Orders", and click "Request Exchange" next to the relevant order. Alternatively, you can contact our support team via WhatsApp at +91 98765 43210 or email support@yourbrand.com with your order number, the item you wish to exchange, and the preferred replacement. We will process your request within 24-48 hours.'
                },
                {
                    question: 'Do I have to pay for return shipping?',
                    answer: 'For your first exchange on any order, return shipping is completely free — we will arrange a reverse pickup from your address or provide a prepaid shipping label. For subsequent exchanges on the same order, a nominal shipping fee of \u20B9149 may apply. Damaged or defective items are always exchanged free of charge.'
                },
                {
                    question: 'How long does the exchange process take?',
                    answer: 'Once you initiate an exchange request, our team reviews it within 24-48 hours. After approval, we arrange a reverse pickup within 2-3 business days. Once we receive and inspect the returned item, the replacement is dispatched within 2-3 business days. The entire process typically takes 10-14 business days from request to delivery of the new item.'
                },
                {
                    question: 'Can I get a refund instead of an exchange?',
                    answer: 'Yes, refunds are available for eligible items. Once we receive the returned product and complete our quality inspection, the refund is processed within 5-7 business days. For online payments, the amount is credited to your original payment method. For COD orders, refunds are processed via bank transfer to the account details you provide.'
                }
            ]
        },
        {
            label: 'Products',
            questions: [
                {
                    question: 'How do I find my correct size?',
                    answer: 'Each product page features a detailed size guide with measurements in both inches and centimetres. We recommend measuring yourself with a tape measure and comparing your measurements to our size chart. If you are between sizes, we generally recommend going one size up for a comfortable fit. For bespoke orders, you can provide your exact measurements during checkout.'
                },
                {
                    question: 'Do you offer custom sizing or made-to-measure garments?',
                    answer: 'Yes, we offer custom sizing on select products. Look for the "Custom Size" option on the product page. You will be prompted to enter your specific measurements including bust, waist, hips, shoulder width, and garment length. Custom-sized garments require an additional 10-15 business days for production and are not eligible for exchange.'
                },
                {
                    question: 'Are the product colours accurate on the website?',
                    answer: 'We make every effort to display colours as accurately as possible. However, actual colours may vary slightly from what you see on screen due to differences in monitor settings, display calibrations, and lighting conditions during photography. The fabric composition and care instructions listed on each product page are always accurate.'
                },
                {
                    question: 'How should I care for my garments?',
                    answer: 'Care instructions are printed on the label of each garment and also listed on the product page. For most of our embroidered and embellished pieces, we recommend dry cleaning. Plain silks and cotton garments can typically be hand-washed in cold water with a mild detergent. Always store garments in a cool, dry place away from direct sunlight to preserve fabric quality and colour.'
                },
                {
                    question: 'Will sold-out items be restocked?',
                    answer: 'Popular items are restocked regularly based on demand. You can click the "Notify Me" button on any sold-out product page to receive an email alert when it becomes available again. Limited edition and seasonal collections are typically not restocked once sold out, so we recommend ordering early.'
                }
            ]
        },
        {
            label: 'Payments',
            questions: [
                {
                    question: 'What payment methods do you accept?',
                    answer: 'We accept a wide range of payment options through our secure payment partner Razorpay. This includes all major credit and debit cards (Visa, MasterCard, American Express, RuPay), net banking from all major Indian banks, UPI (Google Pay, PhonePe, Paytm, BHIM), popular wallets, and Cash on Delivery (COD) for eligible pincodes.'
                },
                {
                    question: 'Is Cash on Delivery (COD) available?',
                    answer: 'COD is available for orders up to \u20B910,000 delivered to serviceable pincodes within India. A nominal COD handling fee of \u20B949 is applied to all COD orders. COD is not available for international orders, custom-made garments, or during certain promotional events. You can check COD availability for your pincode on the product page.'
                },
                {
                    question: 'Are my payment details secure?',
                    answer: 'Absolutely. All payments are processed through Razorpay, which is PCI-DSS Level 1 certified — the highest level of payment security compliance. Your card details are encrypted and never stored on our servers. We also support 3D Secure (OTP verification) for an additional layer of protection on card transactions.'
                },
                {
                    question: 'Can I pay in EMI or instalments?',
                    answer: 'Yes, we offer EMI options on orders above \u20B93,000 through select bank credit cards via Razorpay. Available EMI tenures range from 3 to 12 months depending on your bank. No-cost EMI is available on select products during special promotions. EMI options are displayed at checkout based on your card eligibility.'
                },
                {
                    question: 'I was charged but my order was not confirmed. What should I do?',
                    answer: 'In rare cases, payment may be deducted but the order may not be confirmed due to a network timeout or technical issue. Please do not place a duplicate order. The amount is typically auto-refunded to your account within 5-7 business days. If you do not receive the refund, please contact our support team with your transaction reference number and we will resolve it immediately.'
                }
            ]
        },
        {
            label: 'Account',
            questions: [
                {
                    question: 'Do I need an account to shop?',
                    answer: 'While you can browse our collections without an account, creating one is required to make a purchase. Having an account allows you to track orders, save items to your wishlist, store multiple shipping addresses, view your order history, and enjoy a faster checkout experience. Registration is quick and only requires your name, email, and mobile number.'
                },
                {
                    question: 'How do I reset my password?',
                    answer: 'Click "Forgot Password" on the login page and enter the email address or mobile number associated with your account. You will receive a one-time password (OTP) or a password reset link. Follow the instructions to set a new password. If you do not receive the OTP within a few minutes, check your spam folder or try again. For further assistance, contact our support team.'
                },
                {
                    question: 'Can I have multiple shipping addresses saved?',
                    answer: 'Yes, you can save multiple shipping addresses in your account under the "Address Book" section. During checkout, simply select the address you want to ship to. You can add, edit, or remove addresses at any time. There is no limit to the number of addresses you can store.'
                },
                {
                    question: 'How do I delete my account?',
                    answer: 'If you wish to delete your account, please contact our support team at support@yourbrand.com with your registered email address and a request for account deletion. We will process your request within 7 business days. Please note that account deletion is permanent and you will lose access to your order history, wishlist, and any store credits.'
                },
                {
                    question: 'Is my personal information safe?',
                    answer: 'We take your privacy very seriously. All personal data is encrypted and stored securely in compliance with Indian data protection regulations. We never sell or share your personal information with third parties for marketing purposes. For complete details on how we handle your data, please refer to our Privacy Policy page.'
                }
            ]
        }
    ];

    const toggleQuestion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        setOpenIndex(null);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>
            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main>
                    {/* Hero Section */}
                    <section className="py-20 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-3xl mx-auto text-center">
                            <div className="w-16 h-16 border border-[#C9A96E]/30 rounded-full flex items-center justify-center mx-auto mb-8">
                                <HelpCircle className="w-7 h-7 text-[#C9A96E]" />
                            </div>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-4xl lg:text-6xl font-light text-[#C9A96E] mb-6 tracking-wide"
                            >
                                Frequently Asked Questions
                            </h1>
                            <p className="text-[#8A8A7A] text-lg max-w-2xl mx-auto leading-relaxed">
                                Find answers to common questions about our products, orders, shipping, and policies.
                            </p>
                        </div>
                    </section>

                    {/* Category Tabs */}
                    <section className="border-b border-[#2A2A2A]">
                        <div className="max-w-5xl mx-auto px-4 lg:px-12">
                            <div className="flex overflow-x-auto no-scrollbar">
                                {categories.map((category, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleTabChange(index)}
                                        className={`px-5 lg:px-8 py-4 lg:py-5 text-sm lg:text-base font-medium whitespace-nowrap transition-all duration-300 border-b-2 ${
                                            activeTab === index
                                                ? 'text-[#C9A96E] border-[#C9A96E]'
                                                : 'text-[#8A8A7A] border-transparent hover:text-[#F5F0EB] hover:border-[#2A2A2A]'
                                        }`}
                                    >
                                        {category.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* FAQ Accordion */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12">
                        <div className="max-w-3xl mx-auto">
                            <div className="space-y-4">
                                {categories[activeTab].questions.map((faq, index) => {
                                    const isOpen = openIndex === index;

                                    return (
                                        <div
                                            key={index}
                                            className={`bg-[#141414] border rounded-lg overflow-hidden transition-all duration-300 ${
                                                isOpen
                                                    ? 'border-l-4 border-l-[#C9A96E] border-t-[#2A2A2A] border-r-[#2A2A2A] border-b-[#2A2A2A]'
                                                    : 'border-[#2A2A2A] hover:border-[#C9A96E]/30'
                                            }`}
                                        >
                                            <button
                                                onClick={() => toggleQuestion(index)}
                                                className="w-full px-6 lg:px-8 py-5 lg:py-6 flex items-center justify-between text-left transition-colors"
                                            >
                                                <span className="text-[#F5F0EB] font-medium text-base lg:text-lg pr-6 leading-relaxed">
                                                    {faq.question}
                                                </span>
                                                <span className="flex-shrink-0">
                                                    {isOpen ? (
                                                        <Minus className="w-5 h-5 text-[#C9A96E]" />
                                                    ) : (
                                                        <Plus className="w-5 h-5 text-[#C9A96E]" />
                                                    )}
                                                </span>
                                            </button>
                                            {isOpen && (
                                                <div className="px-6 lg:px-8 pb-6 lg:pb-8">
                                                    <div className="border-t border-[#2A2A2A] pt-5">
                                                        <p className="text-[#8A8A7A] text-base leading-[1.7]">
                                                            {faq.answer}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>

                    {/* Still Have Questions */}
                    <section className="py-16 lg:py-24 px-4 lg:px-12 border-t border-[#2A2A2A]">
                        <div className="max-w-2xl mx-auto text-center">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Get In Touch</p>
                            <h2
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-3xl lg:text-4xl font-light text-[#F5F0EB] mb-6"
                            >
                                Still Have Questions?
                            </h2>
                            <p className="text-[#8A8A7A] text-lg mb-10 leading-relaxed">
                                Our customer support team is happy to help you with anything not covered here.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => navigate('/template21/contact')}
                                    className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-[#C9A96E] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#D4B87A] transition-colors"
                                >
                                    Contact Us
                                </button>
                                <a
                                    href="https://wa.me/919876543210"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-10 py-4 border border-[#C9A96E] text-[#C9A96E] font-semibold rounded-lg hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    WhatsApp Us
                                </a>
                            </div>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default FAQs21;
